<script setup lang="ts">
import { useApiFetch } from "~/composible/useApiFetch";
import { shorteningNumber } from "~/utils/numbers";
import { defineBreadcrumb, useDevice } from "#imports";
import { useAuth } from "~/composible/useAuth";
import useOneLink from "~/composible/useOneLink";

const { user, getOTT } = useAuth();
const { isMobile } = useDevice();
const { getAuthBookLink } = useOneLink();
const { slug } = useRoute().params;
const config = useRuntimeConfig()
const { $toast } = useNuxtApp();

const [bookResponse, chaptersResponse, similarResponse] = await Promise.all([
  useApiFetch(`/novels/${slug}`),
  useApiFetch(`/novels/${slug}/chapters/1/next`),
  useApiFetch(`/novels/${slug}/similar`)
]);

const { data: bookData, error: bookError, status: bookStatus } = bookResponse;
const { data: chaptersData, error: chaptersError, status: chaptersStatus } = chaptersResponse;
const { data: similarData, pending: pendingSimilar } = similarResponse;

if (bookError.value?.statusCode && bookError.value.statusCode !== 404) {
  throw createError({
    statusCode: bookError.value?.statusCode,
    statusMessage: bookError.value?.message
  })
}

const book = ref(bookData.value?.data);
const similar = ref(similarData.value?.data);
const chapters = ref(chaptersData.value?.data)

const isUploadedByUser = computed(() => book.value && typeof book.value.uploaded_by === "string");

if (isUploadedByUser.value) {
  navigateTo(`/book/${book.value.slug}`, { redirectCode: 301 })
}

const nextChapter = computed(() => {
  if (chapters.value && chapters.value.length >= 2) {
    const sortChapters = chapters.value
        ? chapters.value.sort((a, b) => a.order - b.order)
        : [];

    return sortChapters[0];
  }

  return null;
})

const appLinkLoading = ref(false);
const ott = ref<string|null>(null);
const uuid = ref<string|null>(user.value?.uuid || null);
const goApp = async () => {
  appLinkLoading.value = true;

  try {
    if (user.value) {
      const response = await getOTT();
      ott.value = response.data.token;
    }

    window.location.href = getAuthBookLink(book.value.id, ott.value, uuid.value);
  } catch(error) {
    $toast.warning('Something went wrong.')
  } finally {
    appLinkLoading.value = false;
  }
}

const headPaginationLinks = computed(() => {
  let links = [];
  if (nextChapter.value) {
    links.push({rel: 'next', href: config.public.siteURL + `/books/${book.value.slug}/chapter-${nextChapter.value.order}`})
  }

  return links;
});

const { data: trending, pending: pendingTrending } = useApiFetch('/novels/top-trending', {
  transform: (trending) => trending.data,
  lazy: true,
  server: false,
  immediate: bookError.value?.statusCode === 404
});

const crumbs = ref(<Object[]>[]);

const chapterBlock = ref(null);
const showProscroll = ref(false);
const onPageScroll = () => {
  if (chapterBlock.value) {
    const position = chapterBlock.value.getBoundingClientRect();
    showProscroll.value = isMobile && position.top < 50 && position.bottom > 600;
  }
}

onMounted(() => {
  window.addEventListener('scroll', onPageScroll);
})

onUnmounted(() => {
  window.removeEventListener('scroll', onPageScroll);
})

if (book.value) {
  const genre = book.value.genres
      ? book.value.genres[0]
      : null

  crumbs.value = [
    { link: '/genres/all-novels', label: 'Novels' },
    { link: `/genres/${genre?.slug}-novels`, label: genre?.name },
    { label: book.value.title }
  ];

  const title = `${book.value.title} book by ${book.value?.author}`;
  const description = book.value.description.substring(0, 155);

  useHead({
    link: headPaginationLinks
  });

  useServerSeoMeta({
    titleTemplate: '%s – Read on FictionMe',
    title: title,
    description: description,
    ogTitle: title,
    ogDescription: description,
    twitterTitle: title,
    twitterDescription: description,
  });

  useSchemaOrg([
    defineBreadcrumb({
      itemListElement: [
        defineListItem({
          position: 1,
          name: 'Novels',
          item: '/genres/all-novels'
        }),
        defineListItem({
          position: 2,
          name: genre?.name,
          item: `/genres/${genre?.slug}-novels`
        }),
        defineListItem({
          position: 3,
          name: book.value.title,
        })
      ]
    }),
    defineBook({
      name: book.value.title,
      description: book.value.description,
      publisher: defineOrganization({
        name: "FictionMe",
        url: "https://fictionme.net"
      }),
      inLanguage: "en",
      image: book.value.cover,
      bookFormat: "EBook",
      author: definePerson({
        name: book.value.author
      }),
      aggregateRating: defineAggregateRating({
        bestRating: 5,
        ratingCount: Math.floor(Math.random() * (500 - 100 + 1)) + 100,
        ratingValue: book.value?.rate
      }),
    })
  ]);
}
</script>

<template>
  <div class="container py-8 max-md:py-6">
    <template v-if="bookError?.statusCode !== 404">
      <div class="page-content book-summary flex flex-col gap-8 max-md:gap-6">
        <breadcrumbs :breadcrumbs="crumbs" />

        <div class="book-details flex flex-col p-8 rounded-xl bg-white dark:border-zinc-700 dark:bg-zinc-800 max-md:p-4 gap-8 max-md:gap-4 max-md:rounded-none max-md:mx-[-15px]">
          <div class="grid grid-cols-[auto_1fr] grid-rows-[auto_1fr] grid-flow-row-dense items-start gap-x-8 gap-y-4 max-md:gap-3">
            <div class="book-details__cover md:row-span-2 flex-none rounded-lg overflow-hidden max-md:w-[124px] max-md:h-[173px] h-[405px]">
              <BookCover
                  :path="book.cover_path"
                  :url="book.cover"
                  :alt="`Book cover of “${book.title}“ by ${book.author}`"
                  :quality="80"
                  :width="isMobile ? 124 : 290"
                  :height="isMobile? 173 : 405"
                  format="webp"
                  class="max-md:w-[124px]"
              />
            </div>
            <div class="flex flex-col gap-4 book-details__content">
              <div>
                <h1 class="text-4xl font-bold max-md:font-semibold max-md:text-xl leading-10 max-md:leading-5 mb-2 max-md:mb-3">{{ book?.title }}</h1>
                <ul class="grid gap-3 max-md:gap-2 leading-[19px]">
                  <li v-if="book.genres && book.genres.length > 0">
                    <span class="text-[#5C5C5C]">Genre: </span>
                    <nuxt-link :to="`/genres/${book.genres[0].slug}-novels`" v-if="book?.genres && book.genres.length > 0" class="text-violet-800 dark:text-violet-500">
                      {{ book.genres[0].name }}
                    </nuxt-link>
                  </li>
                  <li v-if="book.age_restriction"><span class="text-[#5C5C5C]">Age:</span> 18+</li>
                  <li><span class="text-[#5C5C5C]">Status:</span> {{ book.is_completed ? 'Completed' : 'Ongoing' }}</li>
                  <li><span class="text-[#5C5C5C]">Language:</span> {{ book.language }}</li>
                  <li><span class="text-[#5C5C5C]">Author:</span> {{ book.author }}</li>
                  <li v-if="book.uploaded_by"><span class="text-[#5C5C5C]">Uploaded by {{ book.uploaded_by }}</span></li>
                </ul>
              </div>
            </div>
            <div class="max-md:col-span-2 flex flex-col gap-4 align-top">
              <tag-wrapper :tags="book.tags" :count="isMobile ? 6 : 8" />

              <div class="text-gray-600 dark:text-gray-400">
                <truncated-text :text="book.description" :max="isMobile ? 200 : 350" :interactive="true" />
              </div>

              <ul class="additional-info">
                <li class="px-7">
                  <span class="text-zinc-900 dark:text-white text-xl leading-5 font-semibold">{{ shorteningNumber(book?.views) }}</span>
                  <span class="max-md:text-sm">Views</span>
                </li>
                <li class="px-1">
                  <star-rating :rating="parseFloat(book.rate)" :size="16" class="mb-1" />
                  <span class="max-md:text-sm">User Rating <span class="text-zinc-900 dark:text-white font-semibold">{{ book.rate }}</span></span>
                </li>
                <li>
                  <button @click.prevent="goApp" :disabled="appLinkLoading" rel="nofollow" class="flex-col px-6 h-[76px] w-full bg-zinc-900 flex justify-center items-center content-center bg-fm-gradient hover:bg-fm-gradient-active text-xl max-md:text-base !text-white">
                    <icon name="fm:download" class="text-2xl text-white" />
                    Download
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div v-if="book.review" class="px-8 w-full max-w-[800px] m-auto max-md:px-4">
          <h2 class="text-3xl font-bold mb-4 max-md:text-2xl">Novel review</h2>
          <nuxt-link
              :to="`/book-reviews/${book.review.slug}`"
              class="group review-box grid grid-cols-[auto_1fr] gap-3 max-md:gap-2 items-center w-full bg-white rounded-xl px-6 max-md:px-4 py-3 dark:border-zinc-700 dark:bg-zinc-800"
          >
            <div class="w-[100px] max-md:w-[80px]">
              <nuxt-img
                  src="/img/review_placeholder.svg"
                  width="100"
                  height="100"
                  loading="lazy"
                  alt="Review icon"
                  class="w-full"
              />
            </div>
            <div class="flex flex-col gap-3 max-md:gap-2">
              <p class="text-xl max-md:text-lg leading-6 max-md:leading-5 line-clamp-2 font-semibold text-violet-800 dark:text-violet-500 group-hover:text-primary">
                {{ book.review.title }}
              </p>
              <p class="text-base text-gray-600 dark:text-gray-400 line-clamp-2 max-md:text-sm max-md:leading-4">
                {{ book.review.description }}
              </p>
            </div>
          </nuxt-link>
        </div>

        <div ref="chapterBlock" class="p-8 max-w-[800px] w-full m-auto max-md:p-4 overflow-hidden">
          <h2 class="text-3xl font-bold mb-8 max-md:text-2xl">{{ book.chapter.title }}</h2>
          <reader :chapter="book.chapter" class="mb-8" />

          <div class="flex flex-col gap-4 max-md:gap-2 items-center justify-center w-full">
            <template v-if="nextChapter">
              <router-link
                  :to="`/books/${book.slug}/chapter-${nextChapter.order}`"
                  class="flex w-2/3 max-md:w-full items-center py-3 px-8 max-md:px-4 justify-center bg-fm-gradient rounded-lg !text-white text-xl hover:bg-fm-gradient-active max-md:text-xl"
              >
                Next Chapter
                <svg class="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </router-link>
              <span class="text-2xl max-md:text-xl font-bold text-gray-400">OR</span>
              <button @click.prevent="goApp" :disabled="appLinkLoading" class="flex w-2/3 max-md:w-full items-center py-3 px-8 max-md:px-4 justify-center text-xl max-md:text-xl border-2 border-primary text-center rounded-xl text-violet-800 dark:text-violet-500 dark:hover:text-violet-600">
                Continue Reading in the 📱APP
              </button>
            </template>
          </div>
        </div>

        <lazy-f-card
            v-if="chapters"
            title="Your Next Chapters"
        >
          <ul class="grid grid-cols-2 max-md:grid-cols-1 gap-x-4 chapter-list">
            <li v-for="chapter in chapters">
              <router-link
                  :to="`/books/${book.slug}/chapter-${chapter.order}`"
                  class="flex justify-between gap-1 rounded-xl"
              >
                <span class="truncate text-nowrap">{{ chapter.title }}</span>
                <icon v-if="!chapter.is_free && !chapter.is_purchased" name="fm:lock" class="text-gray-400" />
              </router-link>
            </li>
          </ul>
        </lazy-f-card>

        <lazy-f-card
            v-if="similar && similar.length > 0"
            title="You might like"
        >
          <div class="flex gap-5 max-md:gap-3 overflow-auto scrollbar-hidden">
            <book-card
                v-for="book in similar"
                :book="book"
                :key="book.id"
            />
          </div>
        </lazy-f-card>

        <lazy-cta-banner />
      </div>

      <lazy-float-download-button
          :show="showProscroll"
          @clicked="goApp"
      />
    </template>
    <template v-else>
      <lazy-f-card>
        <p class="text-xl font-semibold">Book not found!</p>
        <p>Apologies, but we couldn't locate the book you're seeking. Feel free to pick a book from the Top Trending section instead.</p>
      </lazy-f-card>
      <lazy-f-card title="Top Trending" header-tag="h1">
        <div v-loading="pendingTrending" class="grid gap-7 grid-cols-2 max-md:grid-cols-1">
          <lazy-book-detailed-card
              v-for="book in trending"
              :book="book"
              :key="book.id"
          />
        </div>
      </lazy-f-card>
    </template>
  </div>
</template>

<style scoped>
.chapter-list > li > a {
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  @apply px-2 py-4 rounded-lg
}

.chapter-list > li:nth-child(4n+1) > a,
.chapter-list > li:nth-child(4n+2) > a {
  background-color: white;
  @apply dark:bg-zinc-950
}

.chapter-list > li:nth-child(4n+3) > a,
.chapter-list > li:nth-child(4n+4) > a {
  background-color: #F4F4F5;
  @apply dark:bg-zinc-800
}
</style>